import React from 'react';
import NavLink from './NavLink';
import { ChopImage } from '@components/ChopImage';
import { buildStaticImageUrl } from '@utils/imgproxy';
import styled from 'styled-components';
import {
  Box,
  Flex,
  CogIcon,
  CalendarIcon,
  UsersIcon,
  LineChartIcon,
  StarIcon,
  TetrisIcon,
} from '@lifechurch/react-ion';
import Feedback from './Feedback';
import Profile from './Profile';
import Checklist from './Onboarding/Checklist/Checklist';
import { CurrentSubscriberFieldsFragment } from '@generated/types';
import { useTranslation } from 'react-i18next';
import NextStepsNavIcon from './icons/next_steps_nav_icon';

interface NavProps {
  currentSubscriber?: CurrentSubscriberFieldsFragment;
}

export const LeaveLink = styled.a`
  font-weight: ${(props): number => props.theme.fontWeights[2]};
  font-size: ${props => props.theme.fontSizes[2]};
  color: ${props => props.theme.colors.gray50};
  background: ${props => props.theme.colors.gray10};
  border-radius: 26px;
  width: calc(100% - 16px);
  display: block;
  margin-block-start: 0;
  margin-block-end: 24px;
  margin-inline-start: 8px;
  margin-inline-end: 8px;
  padding-block-start: 7px;
  padding-block-end: 8px;
  padding-inline-start: 12px;
  padding-inline-end: 12px;
  text-align: center;

  &:hover,
  &:focus {
    text-decoration: none;
    color: ${props => props.theme.colors.action.hover};
    background: ${props => props.theme.colors.action.light};
  }
`;

const ChopLogoWrapper = styled.div`
  margin-block-start: 24px;
  margin-inline-end: 0;
  margin-block-end: 24px;
  margin-inline-start: 14px;
`;

const Nav: React.FC<NavProps> = ({ currentSubscriber }) => {
  const { t } = useTranslation('common');

  return (
    <Flex data-testid='nav' flexDirection='column' height='100%'>
      <ChopLogoWrapper>
        <ChopImage
          alt={t('church_online_platform')}
          data-testid='chopLogo'
          height={24}
          src={buildStaticImageUrl('static/logos/church_online_platform_logo.png')}
          width={184}
        />
      </ChopLogoWrapper>
      <LeaveLink data-testid='leave' href='/'>
        {t('go_to_host_tools')}
      </LeaveLink>
      <NavLink href='/analytics/overview' icon={LineChartIcon} title={t('common:analytics')} />
      <NavLink href='/services' icon={CalendarIcon} title={t('common:services')} />
      <NavLink href='/moments' icon={StarIcon} title={t('common:moments')} />
      <NavLink href='/next_steps' icon={NextStepsNavIcon} title={t('common:next_steps')} />
      <NavLink href='/users' icon={UsersIcon} title={t('common:users')} />
      <NavLink href='/integrations' icon={TetrisIcon} title={t('common:integrations')} />
      <NavLink href='/setup/appearance' icon={CogIcon} title={t('common:setup')} />
      <Box marginBlockStart={2} mx={3}>
        <Checklist />
      </Box>
      <Box marginBlockStart='auto' paddingBlockEnd={5} paddingBlockStart={6} position='relative'>
        <Box alignItems={'center'} display={'flex'} flexDirection={'row'} marginInlineStart='16px'>
          <Profile currentSubscriber={currentSubscriber} />
          <Box marginInlineStart={'12px'}>
            <Feedback />
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default Nav;
